// public/js/features/pois/settings-pois.js
// Settings → POIs: searchable/filterable list of all visible POIs. Row click
// opens the same modal used for creating, pre-filled for editing.
import { loadPoiData } from './api.js';
import { primePoiData } from './poi-data.js';
import { can, getCurrentUser } from '../../modules/rbac.js';

let allPois = [];
let typeMap = new Map();
let canManagePublic = false; // only platform admins manage public POIs
const filterState = { term: '', typeIds: null /* null = all */ };

export async function renderPoisSettings(container) {
  const groups = getCurrentUser().groups || [];
  if (!can(groups, 'listPois')) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }
  canManagePublic = can(groups, 'managePublicPois');
  const canAdd = can(groups, 'createPoi') || canManagePublic;

  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">POIs <span class="badge bg-primary ms-2" id="poi-count-badge">0</span></h5>
      ${canAdd ? `<button id="poi-add-on-map" class="btn btn-success btn-sm"><i class="fa-solid fa-plus me-1"></i>Add on map</button>` : ''}
    </div>
    <div id="poi-filter-slot"></div>
    <div class="card shadow-sm">
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr><th>Name</th><th>Short</th><th>Type</th><th>Scope</th><th>Coordinates</th><th>Geofence</th></tr>
          </thead>
          <tbody id="poi-settings-tbody"></tbody>
        </table>
      </div>
    </div>`;

  container.querySelector('#poi-add-on-map')?.addEventListener('click', () => {
    window.location.hash = '#map';
    setTimeout(() => window.poiStartPlace?.(), 600);
  });

  // Reload hook used by the modal after create/edit/delete.
  window.refreshPoiList = () => load(container);

  await load(container);
}

async function load(container) {
  const tbody = container.querySelector('#poi-settings-tbody');
  if (tbody) tbody.innerHTML = `<tr><td colspan="6" class="text-center text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</td></tr>`;
  try {
    const data = await loadPoiData();
    // This page always reads from the backend, so it holds the freshest POI
    // data in the app - hand it to the shared cache instead of dropping it.
    // Without this, opening a POI from THIS list could offer a type list hours
    // out of date.
    primePoiData(data.types, data.pois);
    // Public POIs are managed only on the platform level — hide them from the
    // settings list for everyone else (they remain visible on the map).
    allPois = (data.pois || []).filter(p => canManagePublic || p.scope !== 'public');
    typeMap = new Map(data.types.map(t => [t.poiId, t]));
    renderFilter(container, data.types);
    renderTable(container);
  } catch (e) {
    if (tbody) tbody.innerHTML = `<tr><td colspan="6" class="text-center text-danger py-4">Failed to load POIs</td></tr>`;
  }
}

function renderFilter(container, types) {
  const slot = container.querySelector('#poi-filter-slot');
  if (!slot) return;
  slot.innerHTML = `
    <div class="card shadow-sm mb-3">
      <div class="card-body py-2 px-3">
        <button class="btn btn-sm btn-link text-secondary text-decoration-none px-0 d-flex align-items-center gap-2"
                type="button" data-bs-toggle="collapse" data-bs-target="#poi-filter-collapse">
          <i class="fa-solid fa-filter"></i><span class="fw-semibold">Filters</span>
          <i class="fa-solid fa-chevron-down ms-1"></i>
        </button>
        <div class="collapse" id="poi-filter-collapse">
          <div class="pt-3">
            <input id="poi-filter-search" class="form-control form-control-sm mb-3" placeholder="Search name, short name, description…" value="${esc(filterState.term)}">
            <div class="small fw-semibold text-secondary mb-1">Types</div>
            <div class="d-flex flex-wrap gap-3">
              ${types.map(t => `
                <div class="form-check">
                  <input class="form-check-input poi-filter-type" type="checkbox" id="pf-${t.poiId}" data-type-id="${t.poiId}"
                    ${(!filterState.typeIds || filterState.typeIds.has(t.poiId)) ? 'checked' : ''}>
                  <label class="form-check-label d-flex align-items-center gap-1" for="pf-${t.poiId}">
                    ${swatch(t)} ${esc(t.name)}
                  </label>
                </div>`).join('')}
            </div>
          </div>
        </div>
      </div>
    </div>`;

  const search = slot.querySelector('#poi-filter-search');
  let t;
  search.addEventListener('input', () => {
    clearTimeout(t);
    t = setTimeout(() => { filterState.term = search.value.trim(); renderTable(container); }, 200);
  });
  slot.querySelectorAll('.poi-filter-type').forEach(cb => {
    cb.addEventListener('change', () => {
      const checked = [...slot.querySelectorAll('.poi-filter-type:checked')].map(x => x.dataset.typeId);
      filterState.typeIds = new Set(checked);
      renderTable(container);
    });
  });
}

function renderTable(container) {
  const tbody = container.querySelector('#poi-settings-tbody');
  const badge = container.querySelector('#poi-count-badge');
  if (!tbody) return;

  const lc = filterState.term.toLowerCase();
  const filtered = allPois.filter(p => {
    if (filterState.typeIds && !filterState.typeIds.has(p.typeId)) return false;
    if (lc) {
      const hay = [p.name, p.shortName, p.description].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(lc)) return false;
    }
    return true;
  });

  if (badge) badge.textContent = filtered.length < allPois.length ? `${filtered.length} / ${allPois.length}` : allPois.length;

  if (filtered.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" class="text-center text-muted py-4">${allPois.length === 0 ? 'No POIs yet.' : 'No POIs match the filters.'}</td></tr>`;
    return;
  }

  tbody.innerHTML = filtered.map(p => {
    const type = typeMap.get(p.typeId);
    const coords = (Number.isFinite(+p.lat) && Number.isFinite(+p.lng)) ? `${(+p.lat).toFixed(4)}, ${(+p.lng).toFixed(4)}` : '—';
    // A POI carries a geofence when it has a polygon (>=3 points) or a radius >0
    // (the same test poi-locate.js / the trip engine use).
    const hasPoly = Array.isArray(p.geofencePolygon) && p.geofencePolygon.length >= 3;
    const gRadius = Number(p.geofenceRadiusM);
    const geoCell = hasPoly
      ? '<span class="badge badge-soft-green"><i class="fa-solid fa-draw-polygon me-1"></i>Polygon</span>'
      : (Number.isFinite(gRadius) && gRadius > 0)
        ? `<span class="badge badge-soft-green"><i class="fa-regular fa-circle me-1"></i>${gRadius} m</span>`
        : '<span class="text-muted">—</span>';
    return `
      <tr class="poi-row" data-poi-id="${p.poiId}" style="cursor:pointer;">
        <td>${esc(p.name || '—')}</td>
        <td>${esc(p.shortName || '—')}</td>
        <td>${type ? `<span class="d-inline-flex align-items-center gap-1">${swatch(type)} ${esc(type.name)}</span>` : '<span class="text-muted">—</span>'}</td>
        <td>${p.scope === 'public' ? '<span class="badge bg-info">Public</span>' : '<span class="badge bg-secondary">Tenant</span>'}</td>
        <td><small class="text-muted">${coords}</small></td>
        <td>${geoCell}</td>
      </tr>`;
  }).join('');

  tbody.querySelectorAll('.poi-row').forEach(row => {
    row.addEventListener('click', () => {
      const poi = allPois.find(p => p.poiId === row.dataset.poiId);
      if (poi) window.openPoiModal?.({ mode: 'edit', poi });
    });
  });
}

function swatch(t) {
  return `<span style="display:inline-flex;width:18px;height:18px;border-radius:4px;background:${t.color || '#0d6efd'};color:#fff;align-items:center;justify-content:center;font-size:10px;"><i class="${t.icon || 'fa-solid fa-location-dot'}"></i></span>`;
}

function esc(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
